import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { MapPin, Phone, Clock, Facebook, Instagram, MessageCircle } from "lucide-react";

const SOCIAL_LINKS = [
  { name: "Facebook", icon: Facebook, href: "#" },
  { name: "Instagram", icon: Instagram, href: "#" },
  { name: "WhatsApp", icon: MessageCircle, href: "https://wa.me/12059837363" },
];

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const navItems = [
    ["home", t("nav.home")],
    ["about", t("nav.about")],
    ["departments", t("nav.departments")],
    ["menu", t("nav.menu")],
    ["gallery", t("nav.gallery")],
    ["reviews", t("nav.reviews")],
    ["faq", t("nav.faq")],
    ["visit", t("nav.visit")],
  ];

  return (
    <footer data-testid="site-footer" className="relative bg-[#1a1a18] text-[#F9F6F0]/85 pt-20 pb-10">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 pb-14 border-b border-[#F9F6F0]/10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-5">
              <span className="h-14 w-14 rounded-xl bg-[#F9F6F0] p-0.5 overflow-hidden shadow-sm shrink-0">
                <img
                  src="/images/logo.jpg"
                  alt="La Hacienda Michoacana"
                  className="w-full h-full object-cover rounded-lg"
                />
              </span>
              <div className="font-serif-display text-3xl md:text-4xl text-[#F9F6F0] tracking-tight leading-[1.02]">
                La Hacienda
                <span className="block text-[#C05A3A] italic">Michoacana</span>
              </div>
            </div>
            <p className="mt-5 text-sm max-w-md text-[#F9F6F0]/65 leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#E6A85C] mb-3">
                {t("footer.socialTitle")}
              </div>
              <div className="flex items-center gap-3" data-testid="footer-socials">
                {SOCIAL_LINKS.map(({ name, icon: Icon, href }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={name}
                    data-testid={`footer-social-${name.toLowerCase()}`}
                    className="h-10 w-10 rounded-full border border-[#F9F6F0]/20 hover:border-[#E6A85C] hover:text-[#E6A85C] text-[#F9F6F0]/80 flex items-center justify-center transition-colors"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#E6A85C] mb-4">
              {t("footer.navTitle")}
            </div>
            <ul className="space-y-2.5 text-[14px]">
              {navItems.map(([id, label]) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    data-testid={`footer-nav-${id}`}
                    className="hover:text-[#E6A85C] transition-colors"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#E6A85C] mb-4">
              {t("footer.visitTitle")}
            </div>
            <ul className="space-y-3 text-[14px]">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[#C05A3A]" strokeWidth={1.5} />
                <span>118 Green Springs Hwy, Birmingham, AL 35209</span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[#C05A3A]" strokeWidth={1.5} />
                <a href="tel:+12059837363" className="hover:text-[#E6A85C]">(205) 983-7363</a>
              </li>
              <li className="flex gap-3">
                <Clock className="h-4 w-4 mt-0.5 text-[#C05A3A]" strokeWidth={1.5} />
                <span>{t("common.open")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] text-[#F9F6F0]/45">
          <div>{t("footer.copyright").replace("{year}", year)}</div>
          <div className="flex items-center gap-4">
            <span>EN · ES</span>
            <span>Birmingham, Alabama</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
